import React, {DetailedHTMLProps, HTMLAttributes, PropsWithChildren, SelectHTMLAttributes, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import SelectBox, {Button as ButtonSelectBox, SelectBoxRef} from "devextreme-react/select-box";
import Guid from "../../models/Guid";
import useApi from "../../hooks/useApi";
import IPaiementProfile from "../../models/IPaiementProfile";
import type {dxButtonOptions} from "devextreme/ui/button";
import visa from "./visa.svg";
import mastercard from "./mastercard.svg"
import amex from "./american-express.svg"
import discover from "./discover.svg"
import CarteCreditPopup, {IInfoCarteCredit} from "./carte-credit-popup";
import IClient from "../../models/IClient";
import "./carte-credit.css";
import MessageBox from "../popups/MessageBox";
import {Button, TextBox} from "devextreme-react";
import {useQuery} from "@tanstack/react-query";
import {ErrorBoundary} from "react-error-boundary";

interface ICarteCreditProps {
    idClient: Guid,
    idProfileSelected: Guid | undefined,
    isModeModifier?: boolean,
    onChanged: (selected: IPaiementProfile | undefined) => void,
    afficherOptionAjouterEffacer: boolean,
}

const CarteCredit = ({idClient, idProfileSelected, isModeModifier, onChanged, afficherOptionAjouterEffacer, ...props}: ICarteCreditProps) => {
    const {getData: getPaiementProfiles} = useApi<IPaiementProfile[]>(`Client/${idClient}/PaiementProfiles`)
    const {data: paiementProfiles, refetch: refecthPaiementProfile} = useQuery({
        queryKey: [`paiementProfiles-${idClient}`], queryFn: () => getPaiementProfiles()
    });
    const {postData: postPaiementProfile, fetchData: fetchPaiementProfile} = useApi<IPaiementProfile>(`Client/${idClient}/PaiementProfiles`)
    const {getData: getClient} = useApi<IClient>()
    const {data: client, refetch: refecthClient} = useQuery<IClient | undefined>({
        queryKey: [`client-${idClient}`], queryFn: () => getClient({customPath: `Client/${idClient}`})
    });

    const [isPopupAjouterOpen, setIsPopupAjouterOpen] = useState(false)
    const [isPopupMessageOuvert, setIsPopupMessageOuvert,] = useState(false);
    const selectBoxRef = useRef<SelectBoxRef>(null);
    const ajouterOptions: dxButtonOptions | undefined = useMemo(() => ({
        icon: "plus", stylingMode: "text", onClick: () => {
            setIsPopupAjouterOpen(true)
        }
    }), []);

    const effacerOptions: dxButtonOptions | undefined = useMemo(() => ({
        icon: "minus", stylingMode: "text", onClick: (e) => {
            setIsPopupMessageOuvert(true)
        }
    }), []);

    const textboxStyle = useMemo(() => ({paddingLeft: "0.4rem", display: "flex", gap: "0.5rem", alignSelf: "center", alignItems: "center"}), []);

    const itemRender = useCallback((e: any) => {
        let logoCarte: string;
        switch (e?.cardType) {
            case 'VI':
                logoCarte = visa;
                break;
            case 'MC':
                logoCarte = mastercard;
                break;
            case 'AM':
                logoCarte = amex;
                break;
            case 'NN':
                logoCarte = discover;
                break;
            default:
                logoCarte = '';
                break;
        }
        return (
            <>
                <div className={"tteesstt dx-texteditor-input"} style={{display: "flex", height: "1rem", gap: "0.5rem", width: "100%", alignSelf: "center", alignItems: "center"}}>
                    <img style={{height: "1.5rem"}} src={logoCarte}/>
                    <div style={{flex: 1}} className={"dx-state-readonly"}>{`${e?.lastDigits} (${e?.expirationMonth}/${e?.expirationYear})`}</div>
                    {afficherOptionAjouterEffacer ? <Button stylingMode={"text"} icon={"remove"} className={"red"} onClick={async () => {
                        await fetchPaiementProfile("delete", {customPath: `paiementprofile/${e.idPaiementProfile}`});
                        await refecthPaiementProfile();
                    }}/> : null}
                </div>
            </>)
    }, [fetchPaiementProfile, refecthPaiementProfile, afficherOptionAjouterEffacer])

    const fieldRender = useCallback((e: any) => {

        try {
            let logoCarte: string | undefined;

            switch (e?.cardType) {
                case 'VI':
                    logoCarte = visa;
                    break;
                case 'MC':
                    logoCarte = mastercard;
                    break;
                case 'AM':
                    logoCarte = amex;
                    break;
                case 'NN':
                    logoCarte = discover;
                    break;
                default:
                    logoCarte = undefined;
                    break;
            }
            return (
                <TextBox defaultValue={e && `${e?.lastDigits || ""} (${e?.expirationMonth || ""}/${e?.expirationYear || ""})`} readOnly={true} style={textboxStyle}>
                    <img style={{height: "1.5rem"}} src={logoCarte}/>
                </TextBox>
            )
        } catch {
            return <TextBox defaultValue={""} style={textboxStyle} readOnly={true}/>
        }
    }, [textboxStyle])

    useEffect(() => {
        if (!idClient || !paiementProfiles) return;

        if (paiementProfiles.length > 0 && (!idProfileSelected || idProfileSelected.equals(Guid.empty))) {
            selectBoxRef.current?.instance()?.option("value", paiementProfiles[0].idPaiementProfile)
            onChanged(paiementProfiles[0]);
        }

    }, [idClient, paiementProfiles]);

    const onSelectionChanged = useCallback((e: any) => {
        if (!isModeModifier) return;
        onChanged && onChanged(e.selectedItem);
    }, [onChanged, isModeModifier]);

    const okOnClick = useCallback(async (infoCarteCredit: IInfoCarteCredit) => {
        setIsPopupAjouterOpen(false);
        const profile = await postPaiementProfile(infoCarteCredit);

        await refecthPaiementProfile();
        selectBoxRef.current?.instance().option("value", profile.idPaiementProfile)
        onChanged(profile);

    }, [postPaiementProfile, getPaiementProfiles, onChanged]);

    const annulerOnClick = useCallback(() => setIsPopupAjouterOpen(false), []);

    const onAnnuler = useCallback(() => setIsPopupMessageOuvert(false), []);

    return (
        <div>
            <ErrorBoundary fallback={null}>
                <SelectBox {...props} ref={selectBoxRef} onSelectionChanged={onSelectionChanged} dataSource={paiementProfiles || []} valueExpr={"idPaiementProfile"} value={idProfileSelected} stylingMode={undefined} readOnly={!isModeModifier} fieldRender={fieldRender} itemRender={itemRender}>
                    <ButtonSelectBox name={"dropDown"}/>
                    {afficherOptionAjouterEffacer ? <ButtonSelectBox name={"add"} options={ajouterOptions}/> : null}
                    {afficherOptionAjouterEffacer ? <ButtonSelectBox name={"remove"} options={effacerOptions}/> : null}
                </SelectBox>
                {isPopupAjouterOpen ? <CarteCreditPopup client={client} annulerOnClick={annulerOnClick} okOnClick={okOnClick}/> : null}
                {isPopupMessageOuvert ? <MessageBox title={"Attention"} message={"Êtes-vous sûr de vouloir désélectionner la carte de crédit actuelle? Çela n'enlèvera pas la carte de crédit du dossier client."} buttonType={"OuiNon"} onConfirme={() => {
                    selectBoxRef.current?.instance().option("value", undefined)
                    onChanged(undefined);
                    setIsPopupMessageOuvert(false)
                }} onAnnuler={onAnnuler} isOpen={isPopupMessageOuvert}/> : null}
            </ErrorBoundary>
        </div>

    )
}

export default CarteCredit;